import React, { useEffect, useMemo, useState } from 'react';
import Icon from '../../components/ui/Icon';
import { useNavigate } from 'react-router-dom';
import API from '../../services/api';
import './Analytics.css';
import './TestPlanner.css';

// Modern assessment-planning workflow (docs/UI_UX_ARCHITECTURE.md Admin
// Phase 6): INTENT → BLUEPRINT → AVAILABILITY → REVIEW → EDIT → PREVIEW →
// APPROVE → BUILD → ASSIGN/SCHEDULE. Question SELECTION is entirely
// server-side (POST /tests/planner/preview, college-scoped, randomized,
// deduplicated) — this page never fetches the question bank into the
// browser to pick from. COMMIT reuses the exact existing test/question/
// assignment endpoints (POST /tests, POST /tests/:id/questions, POST
// /tests/:id/assign, PUT /tests/:id/settings, POST /tests/:id/publish) —
// no parallel persistence model.
const PURPOSES = [
  { key: 'screening', label: 'Screening', test_type: 'Aptitude Test' },
  { key: 'practice', label: 'Practice', test_type: 'Practice Test' },
  { key: 'diagnostic', label: 'Diagnostic', test_type: 'Diagnostic Test' },
  { key: 'company_prep', label: 'Company Preparation', test_type: 'Company Specific Test' },
];

const CATEGORY_LABEL = { quantitative: 'Quantitative', logical: 'Logical', verbal: 'Verbal' };
const DIFFICULTY_LABEL = { easy: 'Easy', medium: 'Medium', hard: 'Hard' };

// audit AD-13: the tab used to say "Target" while the panel heading said "Who is this for?" — two
// different words for the same concept. "Audience" reads as the same idea as "who is this for" without
// forcing the two to be verbatim identical (no other step's tab label is a verbatim match of its
// heading either, e.g. "Purpose" / "What kind of assessment is this?").
const STEPS = ['Purpose', 'Audience', 'Structure', 'Topic Mix', 'Difficulty', 'Company Style', 'Review'];

const sumValues = (obj) => Object.values(obj).reduce((a, b) => a + (Number(b) || 0), 0);

const TestPlanner = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [options, setOptions] = useState(null);
  const [batches, setBatches] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(true);

  const [purpose, setPurpose] = useState('screening');
  const [batchId, setBatchId] = useState('');
  const [totalQuestions, setTotalQuestions] = useState(40);
  const [duration, setDuration] = useState(45);
  const [categoryMix, setCategoryMix] = useState({ quantitative: 40, logical: 35, verbal: 25 });
  const [difficultyMix, setDifficultyMix] = useState({ easy: 20, medium: 50, hard: 30 });
  const [companyId, setCompanyId] = useState('');

  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [questionDetails, setQuestionDetails] = useState(null);
  const [removedIds, setRemovedIds] = useState(new Set());
  const [scheduledAt, setScheduledAt] = useState('');
  const [building, setBuilding] = useState(false);
  const [buildError, setBuildError] = useState('');
  const [builtTest, setBuiltTest] = useState(null);

  useEffect(() => {
    Promise.allSettled([
      API.get('/tests/planner/options'),
      API.get('/batches'),
    ]).then(([optRes, batchRes]) => {
      if (optRes.status === 'fulfilled') setOptions(optRes.value.data);
      // audit AD-14: GET /batches returns `batch_name` (every other consumer — Batches.js,
      // BatchAnalytics.js — already reads it that way); this component alone expected `.name` and
      // rendered a blank option for every batch. Normalized once here, at the fetch boundary, rather
      // than scattering `b.name || b.batch_name` through the JSX below.
      if (batchRes.status === 'fulfilled') setBatches((batchRes.value.data || []).map(b => ({ ...b, name: b.batch_name })));
    }).finally(() => setLoadingOptions(false));
  }, []);

  const categoryTotal = sumValues(categoryMix);
  const difficultyTotal = sumValues(difficultyMix);
  const blueprintValid = categoryTotal === 100 && difficultyTotal === 100 && totalQuestions > 0 && duration > 0;

  const runPreview = async () => {
    setPreviewLoading(true);
    setPreviewError('');
    setQuestionDetails(null);
    setRemovedIds(new Set());
    try {
      const res = await API.post('/tests/planner/preview', {
        total_questions: totalQuestions,
        category_mix: categoryMix,
        difficulty_mix: difficultyMix,
        company_id: companyId || null,
      });
      setPreview(res.data);
    } catch (err) {
      setPreviewError(err.response?.data?.message || 'Could not generate a preview right now.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const goToReview = async () => {
    setStep(6);
    await runPreview();
  };

  const loadQuestionReview = async () => {
    if (!preview) return;
    const ids = preview.cells.flatMap(c => c.question_ids);
    try {
      const res = await API.post('/tests/planner/questions', { question_ids: ids });
      setQuestionDetails(res.data);
    } catch (err) {
      setPreviewError('Could not load question details for review.');
    }
  };

  const finalQuestionIds = useMemo(() => {
    if (!preview) return [];
    return preview.cells.flatMap(c => c.question_ids).filter(id => !removedIds.has(id));
  }, [preview, removedIds]);

  const buildAndAssign = async () => {
    setBuilding(true);
    setBuildError('');
    try {
      const purposeMeta = PURPOSES.find(p => p.key === purpose);
      const createRes = await API.post('/tests', {
        test_name: `${purposeMeta.label} Assessment — ${new Date().toLocaleDateString('en-IN')}`,
        test_type: purposeMeta.test_type,
        duration_minutes: duration,
        scheduled_at: scheduledAt ? new Date(scheduledAt).toISOString() : null,
      });
      const testId = createRes.data.test.id;

      await API.post(`/tests/${testId}/questions`, { question_ids: finalQuestionIds });

      if (batchId) {
        await API.post(`/tests/${testId}/assign`, { batch_ids: [batchId] });
      }

      await API.post(`/tests/${testId}/publish`);
      setBuiltTest({ id: testId, name: createRes.data.test.test_name });
    } catch (err) {
      setBuildError(err.response?.data?.message || 'Could not create the assessment. Nothing was assigned.');
    } finally {
      setBuilding(false);
    }
  };

  if (loadingOptions) return <div className="axh-page"><div className="axh-loading"><div className="axh-spinner" />Loading planner…</div></div>;

  if (builtTest) {
    const assignedBatchName = batchId ? (batches.find(b => b.id === batchId)?.name || 'the selected batch') : null;
    return (
      <div className="axh-page">
        <div className="axh-card" style={{ maxWidth: 520, margin: '60px auto', textAlign: 'center' }}>
          <h2>Assessment Created</h2>
          <ul style={{ textAlign: 'left', listStyle: 'none', padding: 0, margin: '16px 0' }}>
            <li><Icon name="success" size={14} /> Built — {finalQuestionIds.length} questions added</li>
            <li>{assignedBatchName ? `Assigned — ${assignedBatchName}` : 'Not assigned to anyone yet'}</li>
            <li>{scheduledAt ? `Scheduled — ${new Date(scheduledAt).toLocaleString()}` : 'Published — available immediately'}</li>
          </ul>
          {!assignedBatchName && (
            <p className="axh-empty" style={{ textAlign: 'left' }}>
              This assessment is published but has no audience yet — open it and assign a batch or students so someone can actually take it.
            </p>
          )}
          <button type="button" className="axh-btn primary" onClick={() => navigate(`/admin/tests/${builtTest.id}`)}>
            {assignedBatchName ? 'View Assessment' : 'Assign This Assessment'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="axh-page">
      <div className="axh-topbar">
        <div><h1>Test Planner</h1><p>Define what you want — Zaptude proposes a blueprint, you review and approve.</p></div>
      </div>

      <div className="tpl-steps" role="tablist" aria-label="Planner steps">
        {STEPS.map((s, i) => (
          <button
            key={s}
            role="tab"
            aria-selected={step === i}
            className={`tpl-step ${step === i ? 'tpl-step--active' : ''} ${i < step ? 'tpl-step--done' : ''}`}
            onClick={() => { if (i <= step || i === 6) setStep(i); }}
            disabled={i > step && i !== 6}
          >
            <span className="tpl-step-num">{i + 1}</span>{s}
          </button>
        ))}
      </div>

      <div className="axh-card tpl-panel">
        {step === 0 && (
          <div>
            <h3>What kind of assessment is this?</h3>
            <div className="tpl-choice-grid">
              {PURPOSES.map(p => (
                <button key={p.key} className={`tpl-choice ${purpose === p.key ? 'tpl-choice--active' : ''}`} onClick={() => setPurpose(p.key)}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 1 && (
          <div>
            <h3>Who is this for?</h3>
            {/* audit AD-13: explain what this step actually does and what skipping it means, and show
                how many students are in each batch (the same live count Batches.js/TestCreateWizard.js
                already use) so the admin can see the real size of the audience before building. */}
            <p className="axh-empty" style={{ padding: 0, textAlign: 'left', marginBottom: 12 }}>
              Pick the batch that will receive this assessment once it's built and published. You can
              leave this on "Choose later" and assign a batch or individual students from the
              assessment's own page afterward.
            </p>
            <label className="tpl-field">
              <span>Batch</span>
              <select value={batchId} onChange={e => setBatchId(e.target.value)}>
                <option value="">Choose later (skip for now)</option>
                {batches.map(b => (
                  <option key={b.id} value={b.id}>{b.name} — {b.student_count} student{b.student_count === 1 ? '' : 's'}</option>
                ))}
              </select>
            </label>
            {batchId && (() => {
              const selected = batches.find(b => b.id === batchId);
              return selected ? (
                <p className="axh-empty" style={{ padding: 0, textAlign: 'left', marginTop: 8 }}>
                  This assessment will be assigned to <strong>{selected.student_count}</strong> student{selected.student_count === 1 ? '' : 's'} in <strong>{selected.name}</strong>.
                </p>
              ) : null;
            })()}
          </div>
        )}

        {step === 2 && (
          <div>
            <h3>Structure</h3>
            <div className="tpl-field-row">
              <label className="tpl-field">
                <span>Question count</span>
                <input type="number" min={1} max={200} value={totalQuestions} onChange={e => setTotalQuestions(parseInt(e.target.value, 10) || 0)} />
              </label>
              <label className="tpl-field">
                <span>Duration (minutes)</span>
                <input type="number" min={1} max={240} value={duration} onChange={e => setDuration(parseInt(e.target.value, 10) || 0)} />
              </label>
            </div>
          </div>
        )}

        {step === 3 && (
          <div>
            <h3>Topic Mix</h3>
            <p className="axh-empty" style={{ padding: 0, textAlign: 'left', marginBottom: 12 }}>Percentages of the total question count, by category.</p>
            {Object.keys(categoryMix).map(cat => (
              <label className="tpl-field" key={cat}>
                <span>{CATEGORY_LABEL[cat] || cat} (%)</span>
                <input type="number" min={0} max={100} value={categoryMix[cat]} onChange={e => setCategoryMix(m => ({ ...m, [cat]: parseInt(e.target.value, 10) || 0 }))} />
              </label>
            ))}
            <p className={`tpl-total ${categoryTotal === 100 ? 'tpl-total--ok' : 'tpl-total--bad'}`}>Total: {categoryTotal}% {categoryTotal !== 100 && '(must equal 100%)'}</p>
          </div>
        )}

        {step === 4 && (
          <div>
            <h3>Difficulty</h3>
            {Object.keys(difficultyMix).map(d => (
              <label className="tpl-field" key={d}>
                <span>{DIFFICULTY_LABEL[d]} (%)</span>
                <input type="number" min={0} max={100} value={difficultyMix[d]} onChange={e => setDifficultyMix(m => ({ ...m, [d]: parseInt(e.target.value, 10) || 0 }))} />
              </label>
            ))}
            <p className={`tpl-total ${difficultyTotal === 100 ? 'tpl-total--ok' : 'tpl-total--bad'}`}>Total: {difficultyTotal}% {difficultyTotal !== 100 && '(must equal 100%)'}</p>
          </div>
        )}

        {step === 5 && (
          <div>
            <h3>Company Style</h3>
            <p className="axh-empty" style={{ padding: 0, textAlign: 'left', marginBottom: 12 }}>
              Only companies with real tagged questions in your pool are shown.
            </p>
            <div className="tpl-choice-grid">
              <button className={`tpl-choice ${!companyId ? 'tpl-choice--active' : ''}`} onClick={() => setCompanyId('')}>General</button>
              {(options?.companies || []).map(c => (
                <button key={c.id} className={`tpl-choice ${companyId === c.id ? 'tpl-choice--active' : ''}`} onClick={() => setCompanyId(c.id)}>
                  {c.name} <span className="tpl-choice-count">({c.question_count})</span>
                </button>
              ))}
              {(options?.companies || []).length === 0 && <p className="axh-empty">No company-tagged questions available yet — using General.</p>}
            </div>
            {companyId && (() => {
              const selected = (options?.companies || []).find(c => c.id === companyId);
              const cats = Object.entries(selected?.by_category || {});
              return cats.length > 0 ? (
                <p className="axh-empty" style={{ padding: 0, textAlign: 'left', marginTop: 12 }}>
                  {selected.name}: {cats.map(([cat, n]) => `${cat[0].toUpperCase()}${cat.slice(1)} ${n} available`).join(', ')}.
                </p>
              ) : null;
            })()}
          </div>
        )}

        {step === 6 && (
          <div>
            <h3>Assessment Blueprint</h3>
            <div className="tpl-blueprint-summary">
              <span><strong>{totalQuestions}</strong> questions</span>
              <span><strong>{duration}</strong> minutes</span>
              <span>Coverage: {Object.entries(categoryMix).filter(([, v]) => v > 0).map(([k, v]) => `${CATEGORY_LABEL[k]} ${v}%`).join(' · ')}</span>
              <span>Difficulty: {Object.entries(difficultyMix).filter(([, v]) => v > 0).map(([k, v]) => `${v}% ${DIFFICULTY_LABEL[k]}`).join(' · ')}</span>
            </div>

            {previewLoading && <div className="axh-loading" style={{ minHeight: 120 }}><div className="axh-spinner" />Generating recommendation…</div>}
            {previewError && <div className="axh-error">{previewError}</div>}

            {preview && !previewLoading && (
              <>
                <div className="tpl-availability-banner">
                  {preview.summary.shortfall_total === 0 ? (
                    <span className="tpl-avail-ok">{preview.summary.selected_total} / {preview.summary.requested_total} available — full coverage</span>
                  ) : (
                    <span className="tpl-avail-warn">
                      {preview.summary.selected_total} / {preview.summary.requested_total} available — {preview.summary.shortfall_total} question shortfall
                    </span>
                  )}
                </div>

                <div className="axh-table-wrap">
                  <table className="axh-table">
                    <thead><tr><th>Topic</th><th>Difficulty</th><th>Requested</th><th>Available</th><th>Selected</th></tr></thead>
                    <tbody>
                      {preview.cells.map((c, i) => (
                        <tr key={i} style={c.shortfall > 0 ? { background: 'var(--z-warning-tint)' } : undefined}>
                          <td>{c.topic_name}</td>
                          <td style={{ textTransform: 'capitalize' }}>{c.difficulty}</td>
                          <td>{c.requested}</td>
                          <td>{c.available}</td>
                          <td>{c.shortfall > 0 ? <span style={{ color: 'var(--z-warning)', fontWeight: 700 }}>{c.selected} ({c.shortfall} short)</span> : c.selected}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="tpl-actions">
                  <button type="button" className="axh-btn" onClick={() => setStep(3)}>Edit Blueprint</button>
                  <button type="button" className="axh-btn" onClick={runPreview}>Regenerate</button>
                  <button type="button" className="axh-btn" onClick={loadQuestionReview}>Preview Questions</button>
                </div>

                {questionDetails && (
                  <div className="tpl-question-review">
                    <h4>Question Review ({finalQuestionIds.length} of {questionDetails.length} kept)</h4>
                    {questionDetails.map(q => (
                      <div key={q.id} className={`tpl-question-row ${removedIds.has(q.id) ? 'tpl-question-row--removed' : ''}`}>
                        <div>
                          <span className="tpl-q-text">{q.question_text}</span>
                          <span className="tpl-q-meta">{q.topic_name} · {q.difficulty}{q.company_name ? ` · ${q.company_name}` : ''}{q.image_path ? ' · chart' : ''}</span>
                        </div>
                        <button
                          type="button"
                          className="axh-btn"
                          onClick={() => setRemovedIds(prev => {
                            const next = new Set(prev);
                            next.has(q.id) ? next.delete(q.id) : next.add(q.id);
                            return next;
                          })}
                        >
                          {removedIds.has(q.id) ? 'Restore' : 'Remove'}
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <label className="tpl-field" style={{ marginTop: 16 }}>
                  <span>Schedule (optional)</span>
                  <input type="datetime-local" value={scheduledAt} onChange={e => setScheduledAt(e.target.value)} />
                </label>

                {buildError && <div className="axh-error">{buildError}</div>}

                <div className="tpl-actions">
                  <button
                    type="button"
                    className="axh-btn primary"
                    disabled={building || finalQuestionIds.length === 0}
                    onClick={buildAndAssign}
                  >
                    {building ? 'Building…' : `Approve & Build (${finalQuestionIds.length} questions)`}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {step < 6 && (
          <div className="tpl-nav">
            {step > 0 && <button type="button" className="axh-btn" onClick={() => setStep(s => s - 1)}>Back</button>}
            {step < 5 && <button type="button" className="axh-btn primary" onClick={() => setStep(s => s + 1)}>Next</button>}
            {step === 5 && <button type="button" className="axh-btn primary" disabled={!blueprintValid} onClick={goToReview}>Generate Recommendation</button>}
          </div>
        )}
      </div>
    </div>
  );
};

export default TestPlanner;
